<template>
  <div class="padding-five-all">
    <b-container fluid>
      <!-- ================= CABECERA ================= -->
      <b-card class="mb-3">
        <b-row>
          <b-col md="4">
            <b>Liquidación:</b> {{ detalle.idLiquidacion }}
          </b-col>

          <b-col md="4">
            <b>Estado:</b>
            <b-badge :variant="badgeEstado(detalle.estado)">
              {{ detalle.estado }}
            </b-badge>
          </b-col>

          <b-col md="4" class="text-right">
            <b-button variant="secondary" @click="$router.back()">
              Volver
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- ================= TABLA OS PADRE ================= -->
      <div class="table-scroll">
        <b-table
          bordered
          show-empty
          :busy="isBusy"
          :items="detalle.ordenes"
          :fields="fields"
          :tbody-tr-class="rowClass"
          details-row
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner />
              <strong>Cargando...</strong>
            </div>
          </template>

          <!-- ESTADO -->
          <template #cell(estado)="row">
            <b-badge :variant="badgeEstado(row.item.estado)">
              {{ row.item.estado }}
            </b-badge>
          </template>

          <!-- OBS PROVEEDOR -->
          <template #cell(obsProveedor)="row">
            {{ row.item.obsProveedor || "-" }}
          </template>

          <!-- OBS BACKOFFICE -->
          <template #cell(obsBackoffice)="row">
            <b-form-textarea
              v-if="requiereConfirmacion(row.item)"
              v-model="row.item._obsTmp"
              rows="3"
              placeholder="Observación del gestor"
              @input="marcarObservada(row.item)"
            />
            <span v-else>{{ row.item.obsBackoffice || "-" }}</span>
          </template>
          <template #cell(penalidad)="row">
            <div class="d-flex align-items-center justify-content-end">
              <span
                v-if="row.item.penalidades && row.item.penalidades.length"
                class="text-danger font-weight-bold mr-2"
                v-b-tooltip.hover
                :title="tooltipPenalidades(row.item)"
              >
                - S/ {{ totalPenalidad(row.item).toFixed(2) }} ({{
                  porcentajePenalidad(row.item)
                }}%)
              </span>

              <span v-else class="text-muted mr-2">—</span>

              <b-button
                v-if="puedeEditarPenalidades"
                size="sm"
                variant="outline-danger"
                @click="abrirModalPenalidades(row.item)"
              >
                Editar
              </b-button>
            </div>
          </template>
          <template #cell(costoSinIgv)="row">
            <b-form-input
              v-if="puedeEditarCosto(row.item)"
              type="number"
              step="0.01"
              min="0"
              class="text-right"
              v-model.number="row.item.costoSinIgv"
              @input="onCambioCosto(row.item)"
            />
            <span v-else>
              {{ row.item.costoSinIgv.toFixed(2) }}
            </span>
          </template>
          <!-- CONFIRMAR -->
          <template #cell(confirmar)="row">
            <b-button
              v-if="mostrarCheck(row.item)"
              size="sm"
              variant="success"
              @click="confirmarObservacion(row.item)"
            >
              ✓
            </b-button>
          </template>
          <template #cell(revertir)="row">
            <b-button
              v-if="row.item._dirty"
              size="sm"
              variant="outline-secondary"
              v-b-tooltip.hover
              title="Revertir al costo original"
              @click="revertirCosto(row.item)"
            >
              ↺
            </b-button>
          </template>

          <!-- ================= OS HIJAS (SIEMPRE VISIBLES) ================= -->
          <template #row-details="row">
            <b-table
              v-if="row.item.detalles && row.item.detalles.length"
              small
              bordered
              responsive
              :items="ordenarHijas(row.item.detalles)"
              :fields="detalleFields"
            >
              <template #cell(cantidad)="row">
                <b-form-input
                  type="number"
                  min="1"
                  step="1"
                  inputmode="numeric"
                  @keydown="soloEnteros"
                  v-model.number="row.item.cantidad"
                  @input="onCambioDetalle(row.item)"
                />
              </template>
              <template #cell(costoSinIgv)="row">
                <b-form-input
                  type="number"
                  step="0.01"
                  min="0"
                  class="text-right"
                  v-model.number="row.item.costoSinIgv"
                  @input="onCambioCostoDetalle(row.item)"
                />
              </template>
            </b-table>
          </template>

          <!-- FOOTER -->
          <template #custom-foot="{ columns }">
            <b-tr class="bg-light text-muted small">
              <b-td>SUB TOTALES</b-td>
              <b-td :colspan="7" />
              <b-td class="text-right font-weight-bold">
                {{ subTotalCantidad }}
              </b-td>
              <b-td />
              <!-- costo unitario NO se totaliza -->
              <b-td />
              <!-- costo unitario NO se totaliza -->
              <b-td class="text-right">{{ subTotalSinIgv }}</b-td>
              <b-td class="text-right">{{ subTotalConIgv }}</b-td>
              <b-td :colspan="columns - 14" />
            </b-tr>

            <b-tr class="bg-warning">
              <b-td :colspan="columns" class="text-center">
                <span class="h5 font-weight-bold mr-3">
                  TOTAL A FACTURAR
                </span>
                <span class="h4 font-weight-bold">
                  {{ totalAFacturar }}
                </span>
              </b-td>
            </b-tr>
          </template>
        </b-table>
      </div>

      <!-- ================= ACCIONES BACKOFFICE ================= -->
      <b-row
        v-if="
          detalle.estado === 'PRE_LIQUIDACION' ||
            detalle.estado === 'LIQUIDACION_RESUELTA'
        "
        class="mt-3"
      >
        <b-col cols="12" class="text-right">
          <b-button
            variant="primary"
            class="mr-2"
            :disabled="!puedeGuardar"
            @click="guardar"
          >
            Guardar
          </b-button>

          <b-button
            variant="warning"
            class="mr-2"
            :disabled="!puedeObservar"
            @click="preLiquidacionObservada"
          >
            Pre-liquidación observada
          </b-button>

          <b-button
            variant="success"
            :disabled="!puedeGenerar"
            @click="generarLiquidacion"
          >
            Generar liquidación
          </b-button>
        </b-col>
      </b-row>
    </b-container>
    <div>
      <b-modal
        id="modal-penalidades"
        title="Editar penalidades"
        size="lg"
        @hidden="onModalPenalidadesHidden"
      >
        <div v-if="penalidadesOS">
          <b-table
            small
            bordered
            :items="penalidadDraft ? [penalidadDraft] : []"
            :fields="penalidadesFields"
          >
            <template #cell(tipoId)="r">
              <!-- Penalidad existente (no viene en catálogo / no hay tipoId) -->
              <div
                v-if="r.item.codigo"
                class="d-flex align-items-start justify-content-between"
              >
                <div class="pr-2 td-wrap">
                  <div class="font-weight-bold">
                    {{ r.item.codigo }}
                  </div>
                  <div class="small text-muted td-wrap">
                    {{ r.item.descripcion }}
                  </div>
                </div>

                <b-badge variant="secondary">Aplicada</b-badge>
              </div>

              <!-- Nueva penalidad -->
              <b-form-select
                v-else
                v-model="r.item.tipoId"
                :options="penalidadesCatalogoOptions"
                @change="onTipoPenalidadChange(penalidadesOS, r.item)"
              />
            </template>

            <template #cell(descripcion)="r">
              <div>
                <div>{{ r.item.descripcion || "-" }}</div>

                <!-- Extras para P01 -->
                <div
                  v-if="
                    tipoSeleccionadoActual &&
                      tipoSeleccionadoActual.requierePrecioMercaderia
                  "
                  class="mt-2"
                >
                  <small class="text-muted"
                    >Precio mercadería devuelta (S/)</small
                  >
                  <b-form-input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="r.item.precioMercaderia"
                    @input="recalcularP01(penalidadesOS, r.item)"
                  />
                </div>

                <div
                  v-if="
                    tipoSeleccionadoActual &&
                      tipoSeleccionadoActual.requiereFlete
                  "
                  class="mt-2"
                >
                  <small class="text-muted">Flete (S/)</small>
                  <b-form-input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="r.item.flete"
                    @input="recalcularP01(penalidadesOS, r.item)"
                  />
                </div>
              </div>
            </template>

            <template #cell(monto)="r">
              <b-form-input
                type="number"
                step="0.01"
                min="0"
                class="text-right"
                v-model.number="r.item.monto"
                @input="marcarPenalidadDirty(penalidadesOS)"
                disabled
              />
            </template>

            <template #cell(acciones)="r">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="eliminarPenalidadModal(r)"
              >
                Eliminar
              </b-button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between mt-2">
            <!-- <b-button
              v-if="puedeAgregarPenalidad(penalidadesOS)"
              variant="danger"
              @click="agregarPenalidad(penalidadesOS)"
            >
              + Agregar penalidad
            </b-button>

            <span v-else class="text-muted small">
              Esta OS ya tiene una penalidad. Solo puedes editarla o eliminarla.
            </span> -->

            <div class="text-right">
              <div class="text-muted small">Total penalidades (OS)</div>
              <div class="font-weight-bold text-danger">
                S/ {{ totalPenalidadModal.toFixed(2) }}
              </div>
            </div>
          </div>
        </div>
        <template #modal-footer>
          <b-button
            variant="secondary"
            @click="$bvModal.hide('modal-penalidades')"
          >
            Cancelar
          </b-button>

          <b-button
            variant="primary"
            :disabled="!puedeGuardarPenalidadModal"
            @click="guardarPenalidadModal"
          >
            Guardar
          </b-button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

export default {
  name: "LiquidacionBackofficeDetalle",

  data() {
    return {
      penalidadesCatalogo: [],
      penalidadesOS: null,
      penalidadDraft: null,
      penalidadesEliminadas: {},
      penalidadesFields: [
        {
          key: "tipoId",
          label: "Tipo",
          thStyle: { width: "320px" },
          tdClass: "text-nowrap",
        },
        { key: "descripcion", label: "Descripción" },
        {
          key: "monto",
          label: "Monto (S/)",
          class: "text-right",
          thStyle: { width: "160px" },
        },
        { key: "acciones", label: "", thStyle: { width: "120px" } },
      ],
      serviceCache: {},
      isBusy: false,
      detalle: {
        idLiquidacion: null,
        estado: null,
        ordenes: [],
      },
      fields: [
        { key: "tienda", label: "Tienda", thStyle: { minWidth: "120px" } },
        {
          key: "detSequenceId",
          label: "Nro. OS",
          thStyle: { minWidth: "130px" },
        },
        {
          key: "estado",
          label: "Estado",
          class: "text-center",
          thStyle: { minWidth: "120px" },
        },
        { key: "cliente", label: "Cliente", thStyle: { minWidth: "220px" } },
        {
          key: "skuServicio",
          label: "SKU Servicio",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "descripcionServicio",
          label: "Servicio",
          thStyle: { minWidth: "220px" },
        },
        {
          key: "skuProducto",
          label: "SKU Producto",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "descripcionProducto",
          label: "Producto",
          thStyle: { minWidth: "260px" },
        },
        {
          key: "cantidad",
          label: "Cant.",
          class: "text-right",
          thStyle: { minWidth: "80px" },
        },
        {
          key: "costoSinIgv",
          label: "Costo S/IGV",
          class: "text-right",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "costoConIgv",
          label: "Costo C/IGV",
          class: "text-right",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "totalSinIgv",
          label: "Total S/IGV",
          class: "text-right",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "totalConIgv",
          label: "Total C/IGV",
          class: "text-right",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "penalidad",
          label: "PENALIDAD",
          class: "text-right text-danger",
        },
        {
          key: "servicioEcommerce",
          label: "E-Com",
          class: "text-center",
          thStyle: { minWidth: "90px" },
        },
        {
          key: "obsProveedor",
          label: "Obs. Proveedor",
          thStyle: { minWidth: "220px" },
        },
        {
          key: "obsBackoffice",
          label: "Obs. Gestor",
          thStyle: { minWidth: "220px" },
        },
        {
          key: "confirmar",
          label: "✓",
          class: "text-center",
          thStyle: { minWidth: "60px" },
        },
        {
          key: "revertir",
          label: "",
          class: "text-center",
          thStyle: { width: "40px" },
        },
      ],

      // ================= OS HIJAS =================
      detalleFields: [
        { key: "orden", label: "#" },
        { key: "skuServicio", label: "SKU Servicio" },
        { key: "descripcionServicio", label: "Servicio" },
        { key: "skuProducto", label: "SKU Producto" },
        { key: "descripcionProducto", label: "Producto" },
        { key: "cantidad", label: "Cant.", class: "text-right" },
        { key: "costoSinIgv", label: "Costo S/IGV", class: "text-right" },
        { key: "costoConIgv", label: "Costo C/IGV", class: "text-right" },
        { key: "totalSinIgv", label: "Total S/IGV", class: "text-right" },
        { key: "totalConIgv", label: "Total C/IGV", class: "text-right" },
      ],
    };
  },

  computed: {
    ...mapState("module", ["username", "perfil"]),
    totalPenalidadModal() {
      if (!this.penalidadDraft) return 0;
      return Number(this.penalidadDraft.monto || 0);
    },
    puedeGuardarPenalidadModal() {
      if (!this.penalidadDraft) return true;

      if (!this.penalidadDraft.tipoId) return false;

      const tipo = this.penalidadesCatalogo.find(
        (x) => x.id === this.penalidadDraft.tipoId,
      );
      if (!tipo) return false;

      if (tipo.reglaTipo === "PRECIO_MERCADERIA_DEVUELTA") {
        const precio = Number(this.penalidadDraft.precioMercaderia || 0);
        const flete = Number(this.penalidadDraft.flete || 0);
        return precio + flete > 0;
      }

      return Number(this.penalidadDraft.monto || 0) >= 0;
    },

    tipoSeleccionadoActual() {
      const tipoId = this.penalidadDraft?.tipoId;
      if (!tipoId) return null;
      return this.penalidadesCatalogo.find((x) => x.id === tipoId) || null;
    },
    penalidadesCatalogoOptions() {
      return [
        { value: null, text: "Seleccione..." },
        ...this.penalidadesCatalogo
          .filter((p) => p.activo)
          .sort((a, b) => a.filaOrigen - b.filaOrigen)
          .map((p) => ({
            value: p.id,
            text: `${p.codigo} - ${p.descripcion}`,
          })),
      ];
    },
    subTotalCantidad() {
      return this.detalle.ordenes.reduce(
        (s, i) => s + Number(i.cantidad || 0),
        0,
      );
    },
    subTotalCostoSinIgv() {
      return this.detalle.ordenes
        .reduce((s, i) => s + Number(i.costoSinIgv || 0), 0)
        .toFixed(2);
    },
    subTotalCostoConIgv() {
      return this.detalle.ordenes
        .reduce((s, i) => s + Number(i.costoConIgv || 0), 0)
        .toFixed(2);
    },
    subTotalSinIgv() {
    return this.detalle.ordenes
        .reduce((s, i) => s + Number(i.totalSinIgv || 0), 0)  // ← era costoSinIgv * cantidad
        .toFixed(2);
},
subTotalConIgv() {
    return this.detalle.ordenes
        .reduce((s, i) => s + Number(i.totalConIgv || 0), 0)  // ← era costoConIgv * cantidad
        .toFixed(2);
},
    totalAFacturar() {
      let totalServicios = 0;
      let totalPenalidades = 0;

      for (const os of this.detalle.ordenes) {
        // OS PADRE
        totalServicios += Number(os.totalSinIgv || 0);
        totalPenalidades += this.totalPenalidad(os);

        //  OS HIJAS
        if (os.detalles && os.detalles.length) {
          for (const d of os.detalles) {
            totalServicios += Number(d.totalSinIgv || 0);
          }
        }
      }

      return (totalServicios - totalPenalidades).toFixed(2);
    },
    puedeGuardar() {
      return this.detalle.estado === "PRE_LIQUIDACION";
    },
    puedeObservar() {
      if (this.detalle.estado !== "PRE_LIQUIDACION") return false;

      if (this.detalle.ordenes.some((o) => o._dirty)) return false;

      const observadas = this.detalle.ordenes.filter(
        (o) => o.estado === "OBSERVADA",
      );

      if (!observadas.length) return false;

      const restantesValidas = this.detalle.ordenes
        .filter((o) => o.estado !== "OBSERVADA")
        .every((o) => o.estado === "ACEPTADA");

      return restantesValidas;
    },
    puedeGenerar() {
      const estadosValidos = ["PRE_LIQUIDACION", "LIQUIDACION_RESUELTA"];

      if (!estadosValidos.includes(this.detalle.estado)) return false;
      else if (this.detalle.ordenes.some((o) => o._dirty)) return false;
      return this.detalle.ordenes.every((o) => o.estado === "ACEPTADA");
    },
    puedeEditarPenalidades() {
      const editables = [
        "PRE_LIQUIDACION",
        "LIQUIDACION_OBSERVADA",
        "LIQUIDACION_RESUELTA",
      ];
      return editables.includes(this.detalle.estado);
    },
  },

  mounted() {
    if (this.perfil !== "HP") {
      this.$router.replace("/404");
      return;
    }
    this.cargarCatalogoPenalidades();
    this.cargarDetalle();
  },

  methods: {
    guardarPenalidadModal() {
      const os = this.penalidadesOS;
      const d = this.penalidadDraft;

      if (!os) return;

      if (!d) {
        this.$set(os, "_eliminarPenalidad", true);
        os.penalidades = [];
        this.marcarPenalidadDirty(os);
        this.$bvModal.hide("modal-penalidades");
        return;
      }

      if (!d.tipoId) {
        this.$bvToast.toast("Debe seleccionar un tipo", {
          variant: "warning",
          solid: true,
        });
        return;
      }

      const tipo = this.penalidadesCatalogo.find((x) => x.id === d.tipoId);
      if (!tipo) {
        this.$bvToast.toast("Tipo inválido", {
          variant: "warning",
          solid: true,
        });
        return;
      }

      os.penalidades = [
        {
          idPenalidad: d.idPenalidad ?? null,
          tipoId: d.tipoId,
          codigo: tipo.codigo, //
          descripcion: tipo.descripcion, //
          porcentaje: tipo.porcentaje ?? 0, //
          monto: Number(d.monto || 0),
          precioMercaderia: Number(d.precioMercaderia || 0),
          flete: Number(d.flete || 0),
        },
      ];

      this.$set(os, "_eliminarPenalidad", false);

      this.marcarPenalidadDirty(os);

      this.$bvModal.hide("modal-penalidades");
    },
    onModalPenalidadesHidden() {
      this.penalidadesOS = null;
      this.penalidadDraft = null;
    },
    eliminarPenalidadModal() {
      const os = this.penalidadesOS;
      if (!os) return;

      // cerrar modal / limpiar draft
      this.penalidadDraft = null;

      this.marcarPenalidadDirty(os);
    },
    recalcularP01(os, p) {
      const precio = Number(p.precioMercaderia || 0);
      const flete = Number(p.flete || 0);

      p.monto = +(precio + flete).toFixed(2);

      this.marcarPenalidadDirty(os);
    },
    async cargarCatalogoPenalidades() {
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/penalidades/catalogo`,
        );
        this.penalidadesCatalogo = data || [];
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message ||
            "Error al cargar catálogo de penalidades",
          { variant: "danger", solid: true },
        );
      }
    },
    onCambioCostoDetalle(d) {
      const IGV = 0.18;

      d.costoSinIgv = Number(d.costoSinIgv || 0);
      d.costoConIgv = +(d.costoSinIgv * (1 + IGV)).toFixed(2);

      d.totalSinIgv = +(d.costoSinIgv * d.cantidad).toFixed(2);
      d.totalConIgv = +(d.costoConIgv * d.cantidad).toFixed(2);

      this.$set(d, "_dirty", true);
    },
    soloEnteros(e) {
      if ([".", ",", "e", "-"].includes(e.key)) {
        e.preventDefault();
      }
    },
    onCambioDetalle(d) {
      const IGV = 0.18;
      if (!Number.isInteger(d.cantidad)) {
        this.$bvToast.toast("La cantidad debe ser entera", {
          variant: "warning",
          solid: true,
        });
      }
      d.costoConIgv = +(d.costoSinIgv * (1 + IGV)).toFixed(2);

      d.totalSinIgv = +(d.costoSinIgv * d.cantidad).toFixed(2);
      d.totalConIgv = +(d.costoConIgv * d.cantidad).toFixed(2);

      this.$set(d, "_dirty", true);
    },
    puedeEditarCosto(os) {
      return (
        this.detalle.estado === "PRE_LIQUIDACION" &&
        !!os.obsProveedor &&
        os.obsProveedor.trim().length > 0 &&
        os.estado !== "ACEPTADA"
      );
    },
    revertirCosto(os) {
      const IGV = 0.18;

      
      os.costoSinIgv = os._costoOriginal;
      os.costoConIgv = +(os._costoOriginal * (1 + IGV)).toFixed(2);

      os.totalSinIgv = +(os.costoSinIgv * os.cantidad).toFixed(2);
      os.totalConIgv = +(os.costoConIgv * os.cantidad).toFixed(2);

      
      os.estado = os._estadoOriginal;

   
      os._obsTmp = os._obsBackofficeOriginal || null;

     
      os._dirty = false;

      this.$bvToast.toast("Cambios revertidos", {
        variant: "info",
        solid: true,
      });
    },
    onCambioCosto(os) {
      const IGV = 0.18;

      os.costoSinIgv = Number(os.costoSinIgv || 0);
      os.costoConIgv = +(os.costoSinIgv * (1 + IGV)).toFixed(2);

      os.totalSinIgv = +(os.costoSinIgv * os.cantidad).toFixed(2);
      os.totalConIgv = +(os.costoConIgv * os.cantidad).toFixed(2);

   
      this.$set(os, "_dirty", true);
    },
    porcentajePenalidad(os) {
      if (!os.penalidades || !os.penalidades.length) return 0;
      return Number(os.penalidades[0].porcentaje || 0);
    },
    tooltipPenalidades(os) {
      return os.penalidades
        .map((p) => `• ${p.descripcion}: S/ ${p.monto.toFixed(2)}`)
        .join("\n");
    },
    totalPenalidad(os) {
      if (!os.penalidades || !os.penalidades.length) return 0;

      return os.penalidades.reduce((sum, p) => sum + Number(p.monto || 0), 0);
    },
    async obtenerCostosServicio(skuServicio, tiendaCodigo) {
      const key = `${skuServicio}-${tiendaCodigo}`;

      if (this.serviceCache[key]) {
        return this.serviceCache[key];
      }

      const { data } = await axios.get(
        `${process.env.VUE_APP_REPORT_VTEX_BASE}/hp/service-system/v1/liquidaciones/servicio-adicional`,
        {
          params: { skuServicio, tiendaCodigo },
        },
      );

      this.serviceCache[key] = data;
      return data;
    },
    marcarObservada(os) {
      os.estado = "OBSERVADA";

      //  CLAVE
      this.$set(os, "_dirty", true);
    },
    async guardar() {
      const cambios = this.detalle.ordenes.filter(
        (o) => o._dirty || (o.detalles && o.detalles.some((d) => d._dirty)),
      );

      if (!cambios.length) {
        this.$bvToast.toast("No hay cambios para guardar", {
          variant: "info",
          solid: true,
        });
        return;
      }

      try {
        await axios.post(
          `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/centralizado/guardar`,
          {
            idLiquidacionCab: this.detalle.idLiquidacion,
            items: cambios.map((o) => {
              const p =
                o.penalidades && o.penalidades.length ? o.penalidades[0] : null;
              const eliminar = !!o._eliminarPenalidad;

              return {
                idLiquidacionOs: o.idLiquidacionOs,
                estado: o.estado,
                obsBackoffice: o.estado ==="OBSERVADA" ? o._obsTmp : null,
                costoSinIgv: o.costoSinIgv,

                eliminarPenalidad: eliminar,

                penalidad:
                  !eliminar && p && p.tipoId
                    ? {
                        id: p.idPenalidad ?? p.id ?? null,
                        tipoId: p.tipoId,
                        monto: Number(p.monto || 0),
                        exonerada: false,
                        extras: {
                          precioMercaderia: Number(p.precioMercaderia || 0),
                          flete: Number(p.flete || 0),
                        },
                      }
                    : null,

                adicionales: (o.detalles || [])
                  .filter((d) => d._dirty)
                  .map((d) => ({
                    idDetalle: d.idDetalle,
                    cantidad: d.cantidad,
                    costoSinIgv: d.costoSinIgv,
                    obsBackoffice: d.obsBackoffice || null,
                  })),
              };
            }),
            usuario: this.username,
          },
        );

        this.$bvToast.toast("Cambios guardados correctamente", {
          variant: "success",
          solid: true,
        });

        await this.cargarDetalle();
      } catch (e) {
        this.$bvToast.toast(e.response?.data || "Error al guardar cambios", {
          variant: "danger",
          solid: true,
        });
      }
    },
    async generarLiquidacion() {
      try {
        await axios.post(
          `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/centralizado/generar`,
          {
            idLiquidacionCab: this.detalle.idLiquidacion,
            usuario: this.username,
          },
        );

        this.$bvToast.toast("Liquidación generada", {
          variant: "success",
          solid: true,
        });

        this.$router.push("/liquidaciones");
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message ||
            e.response?.data ||
            "Error al generar liquidación",
          { variant: "danger", solid: true },
        );
      }
    },
    mostrarCheck(os) {
      return (
        (this.detalle.estado === "PRE_LIQUIDACION" ||
          this.detalle.estado === "LIQUIDACION_OBSERVADA") &&
        os.estado === "PENDIENTE_REVISION"
      );
    },

    confirmarObservacion(os) {
      os.estado = "ACEPTADA";
      os._obsTmp = null;

    
      this.$set(os, "_dirty", true);

      this.$bvToast.toast("Observación del proveedor confirmada", {
        variant: "success",
        solid: true,
      });
    },
    badgeEstado(estado) {
      return (
        {
          PRE_LIQUIDACION: "primary",
          OBSERVADA: "warning",
          RESUELTA: "success",
          LIQUIDADA: "dark",
        }[estado] || "secondary"
      );
    },

    rowClass(item) {
      if (!item) return "";

      switch (item.estado) {
        case "OBSERVADA":
          return "table-warning"; // naranja
        case "PENDIENTE_REVISION":
          return "table-secondary"; // gris
        case "ACEPTADA":
        default:
          return "";
      }
    },

    requiereConfirmacion(os) {
      return (
        this.perfil === "HP" &&
        this.detalle.estado === "PRE_LIQUIDACION" &&
        !!os.obsProveedor &&
        !os.confirmadaGestor
      );
    },

    ordenarHijas(detalles) {
      if (!detalles) return [];
      return [...detalles].sort((a, b) => a.orden.localeCompare(b.orden));
    },

   async cargarDetalle() {
    this.isBusy = true;

    try {
        const { data } = await axios.get(
            `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/centralizado?idPeriodo=11&page=1&pageSize=50`
        );

        const todasLasOrdenes = data.ordenes || [];

        this.detalle = {
            idLiquidacion:  data.ordenes?.[0]?.idLiquidacionCab ?? null,
            estado:         "PRE_LIQUIDACION",
            resumen:        data.resumen,
            totalRegistros: data.totalRegistros,
            page:           data.page,
            pageSize:       data.pageSize,
            totalPages:     data.totalPages,

            ordenes: todasLasOrdenes
                .filter(o => !o.esAdicional)
                .map(o => {
                    const adicionales = todasLasOrdenes
                        .filter(a => a.esAdicional && a.detSequenceId === o.detSequenceId);

                    return {
                        // ── Identidad
                        idLiquidacionCab: o.idLiquidacionCab,
                        idLiquidacionOs:  o.idLiquidacionOs,
                        detSequenceId:    o.detSequenceId,

                        // ── Tienda / cliente
                        tienda:           o.nombreTienda,
                        codigoTienda:     o.codigoTienda,
                        cliente:          o.cliente,
                        tipoCliente:      o.tipoCliente,

                        // ── Servicio / producto
                        skuServicio:          o.skuServicio,
                        descripcionServicio:  o.descripcionServicio,
                        skuProducto:          o.skuProducto,
                        descripcionProducto:  o.descripcionProducto,

                        // ── Cantidades y costos — nombres normalizados
                        cantidad:     o.cantidad          ?? 0,
                        costoSinIgv:  o.costoTotalSinIgv  ?? 0,  // ← normalizado
                        costoConIgv:  o.costoTotalConIgv  ?? 0,  // ← normalizado
                        totalSinIgv:  o.totFacturarSinIgv ?? 0,  // ← normalizado
                        totalConIgv:  o.totFacturarConIgv ?? 0,  // ← normalizado

                        // ── Penalidad
                        penalidad:    o.penalidad ?? 0,
                        penalidades:  o.penalidad > 0
                            ? [{ monto: o.penalidad, descripcion: "Penalidad", porcentaje: 0 }]
                            : [],

                        // ── Observaciones
                        obsProveedor:   o.obsProveedor  ?? null,
                        obsBackoffice:  o.obsGestor     ?? null,  // ← normalizado
                        obsGestor:      o.obsGestor     ?? null,

                        // ── Estado
                        estado:           o.estado,
                        servicioEcommerce: null,
                        confirmadaGestor:  false,

                        // ── Adicionales (hijas)
                        detalles:      adicionales.map(a => ({
                            idLiquidacionOs:     a.idLiquidacionOs,
                            orden:               a.orden ?? "-",
                            skuServicio:         a.skuServicio,
                            descripcionServicio: a.descripcionServicio,
                            skuProducto:         a.skuProducto,
                            descripcionProducto: a.descripcionProducto,
                            cantidad:            a.cantidad    ?? 0,
                            costoSinIgv:         a.costoTotalSinIgv ?? 0,
                            costoConIgv:         a.costoTotalConIgv ?? 0,
                            totalSinIgv:         a.totFacturarSinIgv ?? 0,
                            totalConIgv:         a.totFacturarConIgv ?? 0,
                            obsProveedor:        a.obsProveedor ?? null,
                            _dirty:              false,
                        })),

                        // ── Flags internos
                        _costoOriginal:         o.costoTotalSinIgv  ?? 0,
                        _estadoOriginal:         o.estado,
                        _obsBackofficeOriginal:  o.obsGestor         ?? null,
                        _obsTmp:                 o.obsGestor         ?? "",
                        _dirty:                  false,
                        _showDetails:            adicionales.length > 0,
                        _eliminarPenalidad:      false,
                    };
                })
        };

    } catch (e) {
        this.$bvToast.toast(
            e.response?.data?.message || "Error al cargar detalle",
            { variant: "danger", solid: true }
        );
    } finally {
        this.isBusy = false;
    }
},

    async confirmarFila(os) {
      if (!os._obsTmp && os.obsProveedor) {
        this.$bvToast.toast(
          "Debe confirmar la observación del proveedor (ingrese sustento si corrige)",
          { variant: "warning", solid: true },
        );
        return;
      }

      try {
        await axios.post(
          `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/backoffice/observacion`,
          {
            detSequenceId: os.detSequenceId,
            obsBackoffice: os._obsTmp,
            estadoDestino: "RESUELTA",
            usuario: this.username,
          },
        );

        this.$bvToast.toast("Observación confirmada", {
          variant: "success",
          solid: true,
        });

        this.cargarDetalle();
      } catch (e) {
        this.$bvToast.toast(e.response?.data || "Error al confirmar", {
          variant: "danger",
          solid: true,
        });
      }
    },
    async preLiquidacionObservada() {
      try {
        await axios.post(
          `${process.env.VUE_APP_REPORT_VTEX_BASE}` +
            `/hp/service-system/v1/liquidaciones/centralizado/observar`,
          {
            idLiquidacionCab: this.detalle.idLiquidacion,
            usuario: this.username,
          },
        );

        this.$bvToast.toast("Pre-liquidación observada", {
          variant: "success",
          solid: true,
        });

        await this.cargarDetalle();
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message ||
            e.response?.data ||
            "Error al observar la pre-liquidación",
          { variant: "danger", solid: true },
        );
      }
    },
    abrirModalPenalidades(os) {
      if (!os.penalidades) this.$set(os, "penalidades", []);

      this.penalidadesOS = os;

      const p0 = os.penalidades[0];

      // draft (copia)
      this.penalidadDraft = p0
        ? {
            idPenalidad: p0.idPenalidad ?? p0.id ?? null,
            tipoId: p0.tipoId ?? null,
            codigo: p0.codigo ?? null,
            descripcion: p0.descripcion ?? "",
            monto: Number(p0.monto ?? 0),
            porcentaje: p0.porcentaje ?? null,
            precioMercaderia: Number(p0.precioMercaderia ?? 0),
            flete: Number(p0.flete ?? 0),
          }
        : (this.penalidadDraft = {
            idPenalidad: null,
            tipoId: null,
            codigo: null,
            descripcion: "",
            monto: 0,
            porcentaje: null,
            precioMercaderia: 0,
            flete: 0,
          });

      this.$bvModal.show("modal-penalidades");
    },

    eliminarPenalidad(os, p) {
      const id = p.idPenalidad ?? p.id ?? null;

      if (id) {
        if (!this.penalidadesEliminadas[os.idLiquidacionOs]) {
          this.$set(this.penalidadesEliminadas, os.idLiquidacionOs, []);
        }
        this.penalidadesEliminadas[os.idLiquidacionOs].push(id);
      }

      os.penalidades = os.penalidades.filter((x) => x !== p);
      this.marcarPenalidadDirty(os);
    },

    marcarPenalidadDirty(os) {
      this.$set(os, "_dirty", true);
    },

    puedeAgregarPenalidad(os) {
      if (!this.puedeEditarPenalidades) return false;

      const actuales = os?.penalidades || [];
      return actuales.length === 0; // solo si no queda ninguna
    },

    tooltipAgregarPenalidad(os) {
      if (!this.puedeEditarPenalidades)
        return "No se puede editar en este estado";
      const actuales = (os?.penalidades || []).filter((p) => !p._deleted);
      return actuales.length > 0
        ? "Esta OS ya tiene una penalidad. Solo puedes editarla o eliminarla."
        : "Agregar penalidad";
    },

    agregarPenalidad(os) {
      if (!this.puedeAgregarPenalidad(os)) {
        this.$bvToast.toast(
          "Esta OS ya tiene una penalidad. Solo puedes editarla o eliminarla.",
          { variant: "warning", solid: true },
        );
        return;
      }

      os.penalidades.push({
        idPenalidad: null,
        tipoId: null,
        descripcion: "",
        monto: 0,
      });

      this.marcarPenalidadDirty(os);
    },
    onTipoPenalidadChange(_, p) {
      const tipo = this.penalidadesCatalogo.find((x) => x.id === p.tipoId);
      if (!tipo) return;
      p.descripcion = tipo.descripcion;
      p.porcentaje = tipo.porcentaje;
      /*  p.codigo = tipo.codigo;
      
      p.porcentaje = tipo.porcentaje;*/

      if (tipo.reglaTipo === "PCT_SERVICIO" && tipo.porcentaje != null) {
        const base = Number(this.penalidadesOS?.totalConIgv || 0);
        p.monto = +((base * Number(tipo.porcentaje)) / 100).toFixed(2);
      } else if (tipo.reglaTipo === "PRECIO_MERCADERIA_DEVUELTA") {
        p.precioMercaderia = 0;
        p.flete = 0;
        p.monto = 0;
      } else {
        const base = Number(this.penalidadesOS?.totalConIgv || 0);
        p.monto = +((base * Number(tipo.porcentaje)) / 100).toFixed(2);
      }
    },
  },
};
</script>

<style scoped>
.table-scroll {
  overflow-x: auto;
  white-space: nowrap;
}
</style>
